import { z } from 'zod';
import { msg } from '@/lib/forms/messages';
import { maxCodePoints } from '@/lib/forms/primitives';

/**
 * Verification item (add / edit) — the eleven fields the item editor posts to
 * `POST /verification/sessions/{session_id}/items` and
 * `PUT  /verification/sessions/{session_id}/items/{item_id}`.
 *
 * A module-level const, not a `createSchema(t)` factory: messages resolve
 * lazily at parse time, so this stays stable across language changes.
 *
 * WHAT THE BACKEND ACTUALLY ENFORCES (api/routes/verification.py):
 *   `AddItemRequest` / `UpdateItemRequest` type every text field as bare `str`
 *   and both method lists as `list[str]` — no lengths, no enums. So nothing
 *   below is duplicating a 422 the API would already return; each rule turns a
 *   would-be 500 from asyncpg into visible field feedback.
 *
 *   `update_item` applies each field under `is not None`, with one special case:
 *   `review_judgment` is applied from `model_fields_set`, so it is the only
 *   field that can be explicitly nulled. The editor therefore always sends all
 *   eleven keys, and `reviewJudgment: null` really does clear the column.
 *
 * CAPS ARE DB MIRRORS — database/migrations/019_verification_tables.sql:
 *   :38 quality_record_no          VARCHAR(100)
 *   :39 quality_record_title       VARCHAR(500)
 *   :41 quality_record_no_right    VARCHAR(100)
 *   :42 quality_record_title_right VARCHAR(500)
 * `specification`, `item_name` and `standard_target` are TEXT (:32, :33, :35) —
 * unbounded — so they carry NO max here. Inventing one would be a product
 * decision wearing a mirror's clothes.
 *
 * NOT TRIMMED, DELIBERATELY. The shipped editor posts raw input, and the caps
 * above are Postgres VARCHAR limits that count raw characters — so validating a
 * trimmed string would accept a value the column then rejects. The trimming
 * helpers in lib/forms/primitives are unusable here for the same reason: they
 * trim, and an optional-text helper would map '' to undefined,
 * which would drop keys the update route needs present.
 *
 * The two method arrays are a mutually exclusive pair maintained by the picker
 * (setting a rank removes the key from the other list and APPENDS it to the
 * chosen one, so their order is the operator's click order and is observable in
 * the request body). That invariant is NOT enforced here: a violating item can
 * only arrive from stored data, and rejecting it would strand a record the
 * shipped editor can re-save today.
 */

/** The DB CHECK on verification_items admits exactly these, or NULL (:48-49). */
export const REVIEW_JUDGMENTS = ['required', 'not_required'] as const;

/** A TEXT column: required-ness and length are the caller's business, not the DB's. */
const freeText = z.string();

export const verificationItemSchema = z.object({
  specification: freeText,

  /**
   * PRODUCT DECISION (approved), not a backend mirror: `item_name` is TEXT
   * DEFAULT '' and `AddItemRequest.item_name` defaults to '', so the API
   * happily stores a nameless item. The field has rendered a red asterisk and
   * an sr-only "required" since the shadcn port while enforcing nothing, so an
   * entirely blank item posts today. This makes the label honest.
   *
   * Checked on the trimmed value but NOT trimming the output — a whitespace-only
   * name is as empty as '' to a reader, while rewriting what gets posted would
   * be a payload change this migration is not authorised to make.
   */
  itemName: freeText.refine((value) => value.trim().length > 0, msg('validation.required')),

  classifications: z.array(z.string()),
  standardTarget: freeText,
  verificationMethodsPrimary: z.array(z.string()),
  verificationMethodsSecondary: z.array(z.string()),

  qualityRecordNo: freeText.check(maxCodePoints(100, 'validation.maxLength')),
  qualityRecordTitle: freeText.check(maxCodePoints(500, 'validation.maxLength')),

  /**
   * Pydantic types this `str | None` and passes it straight through, so an
   * off-list value is not a 400 — it reaches the DB CHECK and comes back a 500.
   * The guard is `asReviewJudgment` (VerificationItemEditor.tsx:82-83), which coerces
   * every off-list value to null on each seed — do not delete it as redundant. This
   * enum is belt-and-braces and cannot fire from the UI.
   */
  reviewJudgment: z.enum(REVIEW_JUDGMENTS, msg('validation.invalidSelection')).nullable(),

  qualityRecordNoRight: freeText.check(maxCodePoints(100, 'validation.maxLength')),
  qualityRecordTitleRight: freeText.check(maxCodePoints(500, 'validation.maxLength')),
});

export type VerificationItemFormValues = z.infer<typeof verificationItemSchema>;

/** No transform or pipe above, so input and output coincide — see the useForm call. */
export const emptyItemForm = (): VerificationItemFormValues => ({
  specification: '',
  itemName: '',
  classifications: [],
  standardTarget: '',
  verificationMethodsPrimary: [],
  verificationMethodsSecondary: [],
  qualityRecordNo: '',
  qualityRecordTitle: '',
  reviewJudgment: null,
  qualityRecordNoRight: '',
  qualityRecordTitleRight: '',
});
